import { useEffect, useState } from "react"
import type { AppConfig } from "@ezbatna/core/domain/models"
import { AlertCircle } from "lucide-react"
import { getContainer } from "../lib/container"
import { Alert, Button, Card, CardBody, CardFooter, CardHeader, Input, Spinner, useToast } from "../components/ui"

export function AppConfigPage() {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let cancelled = false
    getContainer()
      .settingsRepository.fetchAppConfig()
      .then((c) => {
        if (!cancelled) setConfig(c)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    try {
      await getContainer().settingsRepository.updateAppConfig(config)
      toast.show("تم حفظ إعدادات التطبيق", "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">إعدادات التطبيق</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">التحكم بالحد الأدنى للإصدار والتحديث الإجباري</p>
      </div>

      <Alert variant="warning">
        <span className="inline-flex items-center gap-2xs">
          <AlertCircle className="size-4" />
          يتحقق تطبيق iOS من هذه القيم عند التشغيل — لا يؤثر التغيير على الجلسات المفتوحة حالياً.
        </span>
      </Alert>

      <Card>
        {loading || !config ? (
          <CardBody>
            <div className="flex items-center justify-center py-2xl">
              <Spinner />
            </div>
          </CardBody>
        ) : (
          <>
            <CardHeader className="flex items-center justify-between gap-md">
              <div>
                <h2 className="text-title font-bold text-text-primary">التحديث الإجباري</h2>
                <p className="mt-2xs text-body-sm text-text-secondary">
                  إجبار المستخدمين على التحديث قبل المتابعة إذا كان إصدارهم أقل من الحد الأدنى
                </p>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-sm">
                <span className="text-label font-semibold text-text-primary">مفعّل</span>
                <input
                  type="checkbox"
                  checked={config.forceUpdate}
                  onChange={(e) => setConfig({ ...config, forceUpdate: e.target.checked })}
                  className="size-5 accent-accent"
                />
              </label>
            </CardHeader>
            <CardBody className="flex flex-col gap-lg">
              <Input
                label="الحد الأدنى للإصدار (minVersion)"
                value={config.minVersion}
                onChange={(e) => setConfig({ ...config, minVersion: e.target.value })}
                placeholder="1.0"
              />
              <Input
                label="رابط المتجر (storeUrl)"
                value={config.storeUrl}
                onChange={(e) => setConfig({ ...config, storeUrl: e.target.value })}
                placeholder="https://apps.apple.com/…"
              />
              <Input
                label="رسالة التحديث (updateMessage)"
                value={config.updateMessage}
                onChange={(e) => setConfig({ ...config, updateMessage: e.target.value })}
                placeholder="يرجى تحديث التطبيق للمتابعة"
              />
            </CardBody>
            <CardFooter className="flex justify-end">
              <Button onClick={handleSave} loading={saving}>
                حفظ
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  )
}
